/* eslint-disable max-lines */
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ContentHeadingData } from 'common/models/content-heading';
import { LanguageTranslationService } from 'common/services/language-translation.service';
import { AccountAdminProgramService } from 'private/app/services/account-admin/account-admin-program.service';
import { CountryOption, ProgramSearchPayload, ProgramSearchResults, UpdateCriteriaData } from 'private/app/models/account-admin-program.model';
import { DynamicNestedFilterService } from 'common/services/dynamic-nested-filter.service';
import { AccountAdminApiService } from 'private/app/services/account-admin/account-admin-api.service';
import { ToastService } from 'common/services/toast.service';
import { Router } from '@angular/router';
import { AppConstants } from 'common/app-constants';
import { Subject } from 'rxjs';
import { EligibilityCriteriaQueryConfig } from '../edit-eligibility-criteria/eligibilityCriteriaQueryConfig.service';
import { AccountAdminEligibilityCriteriaService } from 'private/app/services/account-admin/account-admin-eligibility-criteria.service';
import { CommonUtilsService } from 'private/app/services/common-utils.service';
import { QueryBuilderConfig, Rule, RuleSet } from 'common/libs/angular2-query-builder/query-builder/query-builder.interfaces';

@Component({
    selector: 'hvac-create-eligibility-criteria',
    templateUrl: './create-eligibility-criteria.component.html',
    styleUrls: ['./create-eligibility-criteria.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class CreateEligibilityCriteriaComponent implements OnInit, OnDestroy {
    public fieldCriteriaForm: UntypedFormGroup = new UntypedFormGroup({ criteriaName: new UntypedFormControl('', [Validators.required, this.noWhitespaceValidator]) });
    public breadCrumbData: ContentHeadingData;
    public isLoading: boolean;
    public displayInputField: boolean = true;
    public customerCompanyForm: UntypedFormGroup = new UntypedFormGroup({ customerCompanyId: new UntypedFormControl() });
    public distributorCompanyForm: UntypedFormGroup = new UntypedFormGroup({ distributorCompanyId: new UntypedFormControl() });
    public queryBuilderConfigValues: QueryBuilderConfig = this.eligibilityCriteriaQueryConfigService.QueryBuilderConfigValues as QueryBuilderConfig;
    public queryDataToDisplay: RuleSet;
    public modifiedQueryDataToDisplay: RuleSet;
    public selectedCountryId: string | null = 'US';
    public optionListForState: CountryOption[] = [];
    public bannerType = 'CreateEligibilityCriteria';
    public isCustomerCompanyIDFiltered: boolean = false;
    public isDistributorCompanyIDFiltered: boolean = false;
    public currentPageAfterFilter: number = 1;
    public totalPagesAfterFilter: number = 1;
    public maxInputLimitNameCriteriaField: number = 60;
    ngOnDestroy$ = new Subject();
    public companyFileUploadCleanUpFuncRef: () => void;
    private criteriaDetails: UpdateCriteriaData = this.eligibilityCriteriaQueryConfigService.criteriaDetails;
    private distributorsFileUploadCleanUpFuncRef: () => void;
    constructor(
        public translate: TranslateService,
        public eligibilityCriteriaService: AccountAdminEligibilityCriteriaService,
        public eligibilityCriteriaQueryConfigService: EligibilityCriteriaQueryConfig,
        public accountAdminProgramService: AccountAdminProgramService,
        public queryStringService: DynamicNestedFilterService,
        private readonly accountAdminApiService: AccountAdminApiService,
        private toastService: ToastService,
        private languageTranslationService: LanguageTranslationService,
        private router: Router,
        private commonUtils: CommonUtilsService
    ) {
        this.assignComapniesFileUploadCleanUpFuncRef = this.assignComapniesFileUploadCleanUpFuncRef.bind(this);
        this.assignDistributorsFileUploadCleanUpFuncRef = this.assignDistributorsFileUploadCleanUpFuncRef.bind(this);
    }

    ngOnInit(): void {
        // SUBSCRIPTIONS
        this.languageTranslationService.translationsLoaded().subscribe(() => {
            this.breadCrumbData = {
                Content: { title: this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.CREATE_ELIGIBILITY_CRITERIA') },
                breadCrumbs: [
                    {
                        title: this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.PARENT_1'),
                        url: '/admin'
                    },
                    {
                        title: this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.PARENT_2'),
                        url: '/Admin/Program'
                    }
                ]
            };
            this.accountAdminProgramService.setProgramAdminBreadCrumData(this.breadCrumbData);
        });
        this.getAllPrograms();
        this.commonUtils.getCountriesEntity().subscribe((countryList) => {
            this.queryBuilderConfigValues.fields.Country.options = countryList.map((country) => ({
                name: country.name,
                value: country.countryId
            }));
            this.optionListForState = countryList.map((country): CountryOption => ({
                code: country.code,
                value: country.countryId.toString()
            }));
        });
    }

    getStateList(selectedCountry: string) {
        this.commonUtils.getStateEntityByCountry(selectedCountry).subscribe((stateList) => {
            if (this.queryBuilderConfigValues.fields.StateProvince) {
                this.queryBuilderConfigValues = {
                    ...this.queryBuilderConfigValues,
                    fields: {
                        ...this.queryBuilderConfigValues.fields,
                        StateProvince: {
                            ...this.queryBuilderConfigValues.fields.StateProvince,
                            options: stateList.map((state) => ({
                                name: state.name,
                                value: state.stateId
                            }))
                        }
                    }
                };
            }
        });
    }

    filterCustomerID(event: Event) {
        const input = <HTMLInputElement>event.target;
        if (input.value) {
            this.isCustomerCompanyIDFiltered = true;
            this.eligibilityCriteriaService.customerCompanySearchFilterData$.next(
                this.eligibilityCriteriaService.customerCompanyfilteredData$.value.filter((res) => res[0].trim().localeCompare(input.value) === 0)
            );
        }
        else {
            this.isCustomerCompanyIDFiltered = false;
            this.eligibilityCriteriaService.customerCompanySearchFilterData$.next(this.eligibilityCriteriaService.customerCompanyList$.value);
        }
    }

    filterDistributorID(event: Event) {
        const input = <HTMLInputElement>event.target;
        if (input.value) {
            this.isDistributorCompanyIDFiltered = true;
            this.eligibilityCriteriaService.distributorCompanySearchFilterData$.next(
                this.eligibilityCriteriaService.distributorCompanyfilteredData$.value.filter((res) => res[0].trim().localeCompare(input.value) === 0)
            );
        }
        else {
            this.isDistributorCompanyIDFiltered = false;
            this.eligibilityCriteriaService.distributorCompanySearchFilterData$.next(this.eligibilityCriteriaService.distributorCompanyList$.value);
        }
    }


    modifyQueryData($event: RuleSet) {
        const previousCountryId = this.selectedCountryId;
        this.modifiedQueryDataToDisplay = $event;
        this.selectedCountryId = JSON.stringify($event).split('"field":"Country","operator":"Equals","value":')[1]?.split('}')[0];
        this.selectedCountryId = (this.selectedCountryId) ? JSON.parse(this.selectedCountryId) : 'US';
        const countrySelected = this.isCountrySelected(this.modifiedQueryDataToDisplay);
        if (!countrySelected) {
            this.clearStateSelection($event);
            this.queryBuilderConfigValues = {
                ...this.queryBuilderConfigValues,
                fields: { ...this.queryBuilderConfigValues.fields }
            };
            delete this.queryBuilderConfigValues.fields['StateProvince'];

            return;
        }
        if (previousCountryId !== this.selectedCountryId && this.modifiedQueryDataToDisplay && previousCountryId !== null) {
            this.clearStateSelection($event);
        }
        this.queryBuilderConfigValues = {
            ...this.queryBuilderConfigValues,
            fields: {
                ...this.queryBuilderConfigValues.fields,
                StateProvince: {
                    type: 'category',
                    name: this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.PROGRAM_ELIGIBILITY_CRITERIA.CRITERIA_OPTIONS.STATE'),
                    operators: [
                        this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.PROGRAM_ELIGIBILITY_CRITERIA.CRITERIA_OPTIONS.EQUALS')
                    ],
                    options: []
                }
            }
        };
        const allSelectedCountries = this.getAllSelectedCountries($event);
        this.loadStatesForCountries(allSelectedCountries);
    }

    public getAllPrograms() {
        const criteriaDataForPages: ProgramSearchPayload = <ProgramSearchPayload>({
            criteria: {
                programName: '',
                programCode: '',
                programStatus: '',
                brandName: '',
                eligibleCriteriaName: ''
            },
            pageParams: {
                page: 0,
                pageSize: 0
            },
            sortFields: [],
            sortParams: [],
            determineTotalCount: true
        });
        this.accountAdminApiService.getAccountAdminProgramsBySearch((criteriaDataForPages)).subscribe((programResults: ProgramSearchResults) => {
            this.queryBuilderConfigValues.fields.ProgramId.options = programResults.data.map((program) => ({
                name: program.programName,
                value: program.programId.toString()
            }));
        });
    }

    onSubmit() {
        this.isLoading = true;
        this.criteriaDetails.programIds = [];
        this.criteriaDetails.criteriaName = this.fieldCriteriaForm.controls.criteriaName.value;
        this.criteriaDetails.selectedFields = this.queryStringService.getSelectedFields(this.modifiedQueryDataToDisplay);
        this.criteriaDetails.criteriaFilter = this.queryStringService.basicRulesetToQueryString(this.modifiedQueryDataToDisplay);
        this.criteriaDetails.dealerCompanyIds = this.eligibilityCriteriaService.customerCompanyfilteredData$.value.flat(1).map(Number);
        this.criteriaDetails.distributorCompanyIds = this.eligibilityCriteriaService.distributorCompanyfilteredData$.value.flat(1).map(Number);
        if (JSON.stringify(this.modifiedQueryDataToDisplay).includes('ProgramId')) {
            const programId = JSON.stringify(this.modifiedQueryDataToDisplay).split('"field":"ProgramId","operator":"Equals","value":')[1]?.split('}')[0];
            this.criteriaDetails.programIds.push(Number(JSON.parse(programId)));
        }
        this.accountAdminApiService.postCreateEligibilityCriteria(this.criteriaDetails).subscribe((response) => {
            this.accountAdminProgramService.smoothScrollToTop();
            if (response.id) {
                this.isLoading = false;
                this.toastService.add({
                    content: this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.PROGRAM_ELIGIBILITY_CRITERIA.CRITERIA_CREATE_SUCCESS', { criteriaName: response.name }),
                    theme: 'success',
                    id: this.bannerType,
                    closeable: true,
                    autoClose: true
                });
                setTimeout(() => {
                    this.router.navigate([`/Admin/Program/search-program/edit-eligibility-criteria`], {
                        queryParams: {
                            criteriaName: response.name,
                            criteriaId: response.id
                        }
                    });

                    return;
                }, AppConstants.timeOutNavigation);
            }
        },
        (err) => {
            this.isLoading = false;
            this.accountAdminProgramService.smoothScrollToTop();
            this.toastService.add({
                content: err.error.details,
                theme: 'error',
                id: this.bannerType,
                closeable: true,
                autoClose: true
            });
        });
    }

    onCancel() {
        this.fieldCriteriaForm.controls.criteriaName.setValue('');
        this.fieldCriteriaForm.controls.criteriaName.setErrors(null);
        this.fieldCriteriaForm.controls.criteriaName.markAsTouched();
        this.fieldCriteriaForm.controls.criteriaName.updateValueAndValidity();
        this.queryDataToDisplay = this.eligibilityCriteriaQueryConfigService.emptyQueryData;
        this.queryDataToDisplay.rules = [];
        this.eligibilityCriteriaService.getCustomerIdValue([]);
        this.eligibilityCriteriaService.getDistributorIdValue([]);
        this.accountAdminProgramService.smoothScrollToTop();
        this.eligibilityCriteriaService.isCustomerTableVisible = false;
        this.eligibilityCriteriaService.isDistributorTableVisible = false;
        this.companyFileUploadCleanUpFuncRef();
        this.distributorsFileUploadCleanUpFuncRef();
    }

    assignComapniesFileUploadCleanUpFuncRef(cleanUpFunc: () => void) {
        this.companyFileUploadCleanUpFuncRef = cleanUpFunc;
    }

    assignDistributorsFileUploadCleanUpFuncRef(cleanUpFunc: () => void) {
        this.distributorsFileUploadCleanUpFuncRef = cleanUpFunc;
    }

    hasCompanyIds(): boolean {
        return (this.eligibilityCriteriaService.customerCompanyfilteredData$.value?.length || 0) > 0
            || (this.eligibilityCriteriaService.distributorCompanyfilteredData$.value?.length || 0) > 0;
    }

    isRulesetObject(obj: unknown): obj is RuleSet {
        return Array.isArray((obj as RuleSet)?.rules);
    }

    hasEmptyRuleset(ruleset: RuleSet): boolean {
        if (!ruleset || ruleset?.rules?.length === 0) {
            return true;
        }

        let isAnyRulesetEmpty = false;

        for (let i = 0; i < (ruleset?.rules?.length || 0); i++) {
            if (isAnyRulesetEmpty) {
                break;
            }

            const rule = ruleset?.rules[i];

            if (this.isRulesetObject(rule)) {
                isAnyRulesetEmpty = this.hasEmptyRuleset(rule);
            }
            else {
                isAnyRulesetEmpty = this.isRuleValueEmpty(rule);
            }
        }

        return isAnyRulesetEmpty;
    }

    isRuleValueEmpty(rule: Rule): boolean {
        const { value } = rule;
        if (value === null || typeof value === 'undefined') {
            return true;
        }

        return typeof value === 'string' && value.trim().length === 0;
    }

    ngOnDestroy(): void {
        this.eligibilityCriteriaService.ngOnDestroy();
    }

    private noWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
        const value = control.value;
        if (!value) {
            return null;
        }
        if (value.trim().length === 0) {
            return { whitespace: true };
        }
        if (value !== value.trim()) {
            return { leadingTrailingSpaces: true };
        }

        return null;
    }

    private clearStateSelection(ruleSet: RuleSet) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const clearStateFromRules = (rules: any[]): any[] => rules.filter((rule) => rule.field !== 'StateProvince').map((rule) => (
            Array.isArray(rule.rules)
                ? {
                    ...rule,
                    rules: clearStateFromRules(rule.rules)
                }
                : rule
        ));
        ruleSet.rules = clearStateFromRules(ruleSet.rules);
        this.modifiedQueryDataToDisplay = { ...ruleSet };
    }

    private getAllSelectedCountries(ruleSet: RuleSet): string[] {
        const countries: string[] = [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const extractCountries = (rules: any[]) => {
            rules.forEach((rule) => {
                if (rule.field === 'Country' && rule.value) {
                    const countryCode = this.optionListForState.find((entry) => String(entry.value) === rule.value)?.code;
                    if (countryCode && !countries.includes(countryCode)) {
                        countries.push(countryCode);
                    }
                }
                if (rule.rules) {
                    extractCountries(rule.rules);
                }
            });
        };
        extractCountries(ruleSet.rules);

        return countries;
    }

    private loadStatesForCountries(countryCodes: string[]) {
        if (countryCodes.length === 0) {
            return;
        }
        const statePromises = countryCodes.map((code) => this.commonUtils.getStateEntityByCountry(code).toPromise().catch(() => []));
        Promise.all(statePromises).then((stateArrays) => {
            const allStates = stateArrays.flat().map((state) => ({
                name: state.name,
                value: state.stateId
            }));
            this.queryBuilderConfigValues = {
                ...this.queryBuilderConfigValues,
                fields: {
                    ...this.queryBuilderConfigValues.fields,
                    StateProvince: {
                        ...this.queryBuilderConfigValues.fields.StateProvince,
                        options: allStates
                    }
                }
            };
        });
    }

    private isCountrySelected(ruleSet: RuleSet): boolean {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return ruleSet.rules.some((rule: any) => {
            if (rule.field === 'Country' && rule.value) {
                return true;
            }
            if (rule.rules) {
                return this.isCountrySelected(rule);
            }

            return false;
        });
    }
}
